import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Link, Stack } from '@chakra-ui/react';

import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import InvoicingPaymentLateFeeInput from 'components/InvoicingPaymentLateFeeInput';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';

import locales from './locales';
import PaymentPeriodInput from './PaymentPeriodInput';

interface OptionsContentProps {
  minLatePaymentFee: number;
}
const OptionsContent: FC<OptionsContentProps> = ({ minLatePaymentFee }) => {
  const { control } = useFormContext<InvoiceFormSchema | QuoteFormSchema>();
  const { featureFlags } = useFeatureFlagsSwitcher();

  return (
    <Stack spacing="space-16">
      {featureFlags.enableInvoicingTradeNameOption ? (
        <Controller
          control={control}
          name="settings.displayTradeName"
          render={({ field }) => (
            <SunshineCard
              isChecked={field.value}
              onChange={field.onChange}
              variant="checkbox"
            >
              <SunshineCard.Content>
                <SunshineCard.Title>
                  {locales.fields.displayTradeName}
                </SunshineCard.Title>
              </SunshineCard.Content>
            </SunshineCard>
          )}
        />
      ) : null}

      <Controller
        control={control}
        name="lateMaxDays"
        render={({ field, fieldState }) => (
          <Form.Field
            error={fieldState.error?.message}
            label={locales.fields.lateMaxDays}
          >
            <PaymentPeriodInput {...field} />
          </Form.Field>
        )}
      />

      <Controller
        control={control}
        name="lateFeePercentage"
        render={({ field, fieldState }) => (
          <Form.Field
            error={fieldState.error?.message}
            hint={locales.formatString(locales.fields.lateFeeHint, {
              min: minLatePaymentFee,
            })}
            label={locales.fields.lateFeePercentage}
            link={
              fieldState.error?.type === 'invalid_type' ? null : (
                <Link
                  color="blue.600"
                  fontWeight="weight-400"
                  href="https://help.shine.fr/fr/articles/4062963-comment-calculer-les-penalites-de-retard-de-paiement"
                  size="sm"
                  target="_blank"
                >
                  {locales.fields.lateFeeLink}
                </Link>
              )
            }
          >
            <InvoicingPaymentLateFeeInput
              {...field}
              minLatePaymentFee={minLatePaymentFee}
            />
          </Form.Field>
        )}
      />

      <Form.Field label={locales.fields.legalMentions}>
        <Controller
          control={control}
          name="settings.legalMentions"
          render={({ field }) => (
            <Form.Textarea
              onChange={(e) => {
                field.onChange([e.target.value]);
              }}
              value={field.value?.[0]}
            />
          )}
        />
      </Form.Field>

      <Controller
        control={control}
        name="settings.issuerEmail"
        render={({ field, fieldState }) => (
          <Form.Field
            error={fieldState.error?.message}
            label={locales.fields.issuerEmail}
          >
            <Form.Input {...field} value={field.value ?? ''} />
          </Form.Field>
        )}
      />
    </Stack>
  );
};

export default OptionsContent;
